import { HttpHeaders, HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { map, catchError, mergeMap, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { EncryptionService } from './encryption.service';
import { NotificacionesService } from './notificaciones.service';
import { Warning, RespuestaLista, RespuestaAPI, ActualizarWarningPayload, CambiarStatusPayload, CrearWarningPayload, FiltrosWarning } from './interfaces/warnings.interface';

@Injectable({
  providedIn: 'root'
})
export class RepseService {
  // Carga masiva de trabajadores
  cargaMasivaTrabajadores(payload: { idPeriodo: number, fileBase64: string }): Observable<any> {
    const url = `${this.baseUrl}repse/empresa/ejercicio/periodo/trabajador/carga-masiva`;
    return this.http.post<any>(url, payload, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('cargaMasivaTrabajadores', { exito: false, mensaje: 'Error en la carga masiva' }))
      );
  }
  // Método para obtener la lista de trabajadores por idPeriodo
  getTrabajadoresByPeriodo(idPeriodo: number, page?: number, size?: number): Observable<any> {
    const url = `${this.baseUrl}repse/empresa/ejercicio/periodo/${idPeriodo}/trabajador`;
    let params = new HttpParams();
    let hasParams = false;

    if (page !== undefined && page !== null) {
      params = params.set('page', page.toString());
      hasParams = true;
    }

    if (size !== undefined && size !== null) {
      params = params.set('size', size.toString());
      hasParams = true;
    }

    const options = hasParams ? { ...this.httpOptions, params } : this.httpOptions;

    return this.http.get<any>(url, options)
      .pipe(
        map(response => response),
        catchError(this.handleError('getTrabajadoresByPeriodo', {
          exito: false,
          mensaje: 'Error al obtener trabajadores'
        }))
      );
  }

  // Método para obtener trabajadores usando filtros (convierte a idPeriodo internamente)
  getTrabajadoresByFilters(payload: {
    empresaId: number | null,
    idEjercicio: number,
    numeroMes: number
  }): Observable<any> {
    return this.obtenerIdPeriodo(payload.idEjercicio, payload.numeroMes).pipe(
      switchMap(idPeriodo => {
        if (!idPeriodo) {
          return throwError(() => new Error('No se pudo obtener el ID del período para el ejercicio y mes especificados'));
        }
        return this.getTrabajadoresByPeriodo(idPeriodo);
      }),
      catchError(this.handleError('getTrabajadoresByFilters', { exito: false, mensaje: 'Error al obtener trabajadores' }))
    );
  }

  // Método para obtener el porcentaje de completez de trabajadores por período
  getPorcentajeCompletezTrabajadores(idPeriodo: number): Observable<any> {
    const url = `${this.baseUrl}repse/empresa/ejercicio/periodo/${idPeriodo}/trabajador/porcentajeCompletez`;
    return this.http.get<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('getPorcentajeCompletezTrabajadores', {
          exito: false,
          mensaje: 'Error al obtener porcentaje de completez'
        }))
      );
  }

  // Método para eliminar un trabajador por ID
  eliminarTrabajador(id: number): Observable<any> {
    const url = `${this.baseUrl}repse/empresa/ejercicio/periodo/trabajador/${id}`;
    return this.http.delete<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('eliminarTrabajador', { exito: false, mensaje: 'Error al eliminar trabajador' }))
      );
  }

  private baseUrl = environment.wsRepse;

  private webcontentUrl = environment.wsUrl;

  wsOCR = environment.wsRepse + "webcontent-ocr/";

  // wsOCR = "http://127.0.0.1:8000/webcontent-ocr/"

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + localStorage.getItem('IES-AC-TK')
    })
  };

  constructor(
    private http: HttpClient,
    private encryptionService: EncryptionService,
    private notificacionesService: NotificacionesService
  ) {
  }

  bloquearDesbloquear(payload: any) {
    return this.http.post(
      this.baseUrl + 'repse/sap/bloqueoDesbloqueo',
      payload
    );
  }

  // Método para crear una nueva empresa
  crearEmpresa(empresa: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'repse/empresa', empresa, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('crearEmpresa', []))
      );
  }

  // Método para obtener los detalles de una empresa por ID
  obtenerEmpresa(id: number): Observable<any> {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + (localStorage.getItem('IES-AC-TK'))
      })
    };
    console.log("idEmpresa", id);
    console.log("headers", this.httpOptions);
    const url = `${this.baseUrl}${'repse/empresa?traerSubsidiarias=&idEmpresa'}=${id}`;
    return this.http.get<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('obtenerEmpresa', []))
      );
  }

  // Método para obtener los detalles de una subsidiaria de empresa por ID
  obtenerEmpresaSubidiaria(id: number): Observable<any> {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + (localStorage.getItem('IES-AC-TK'))
      })
    };
    console.log("idEmpresa", id);
    console.log("headers", this.httpOptions);
    const url = `${this.baseUrl}${'repse/empresa?traerSubsidiarias=true&idEmpresa'}=${id}`;
    return this.http.get<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('obtenerEmpresa', []))
      );
  }

  // Método para obtener todas las empresas
  obtenerEmpresas(): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'repse/empresa?traerSubsidiarias=false', this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('obtenerEmpresas', []))
      );
  }

  // Método para actualizar los datos de una empresa existente por ID
  actualizarEmpresa(id: number, empresa: any): Observable<any> {
    const url = `${this.baseUrl}${'repse/empresa'}/${id}`;
    return this.http.put<any>(url, empresa, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('actualizarEmpresa', []))
      );
  }

  // Método para eliminar una empresa por ID
  eliminarEmpresa(id: number): Observable<any> {
    const url = `${this.baseUrl}${'repse/empresa'}/${id}`;
    return this.http.delete<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('eliminarEmpresa', []))
      );
  }

  obtenerSubsidiarias(opciones?: { idEmpresa?: number | null; mostrarInactivas?: boolean }): Observable<any> {
    let params = new HttpParams().set('traerSubsidiarias', 'true');

    if (opciones?.idEmpresa) {
      params = params.set('idEmpresa', String(opciones.idEmpresa));
    }

    if (opciones?.mostrarInactivas) {
      params = params.set('mostrarInactivas', 'true');
    }

    const options = { ...this.httpOptions, params };
    return this.http.get<any>(this.baseUrl + 'repse/empresa', options).pipe(
      map(response => response),
      catchError(this.handleError('obtenerSubsidiarias', { exito: false, data: [] }))
    );
  }

  crearSubsidiaria(subsidiaria: any): Observable<any> {
    const url = `${this.baseUrl}repse/subsidiaria`;
    return this.http.post<any>(url, subsidiaria, this.httpOptions).pipe(
      map(response => response),
      catchError(this.handleError('crearSubsidiaria', { exito: false, mensaje: 'Error al crear subsidiaria' }))
    );
  }

  actualizarSubsidiaria(id: number, subsidiaria: any): Observable<any> {
    const url = `${this.baseUrl}repse/subsidiaria/${id}`;
    return this.http.put<any>(url, subsidiaria, this.httpOptions).pipe(
      map(response => response),
      catchError(this.handleError('actualizarSubsidiaria', { exito: false, mensaje: 'Error al actualizar subsidiaria' }))
    );
  }

  eliminarSubsidiaria(id: number): Observable<any> {
    const url = `${this.baseUrl}repse/subsidiaria/${id}`;
    return this.http.delete<any>(url, this.httpOptions).pipe(
      map(response => response),
      catchError(this.handleError('eliminarSubsidiaria', { exito: false, mensaje: 'Error al eliminar subsidiaria' }))
    );
  }

  // Método para crear ejercicios fiscales para una empresa específica
  crearEjercicio(idEmpresa: number, anios: number[], idSubsidiaria: number): Observable<any> {
    const url = `${this.baseUrl}${'repse/empresa'}/${idEmpresa}/ejercicio`;
    const body = {
      idSubsidiaria: idSubsidiaria,
      anios: anios
    };
    return this.http.post<any>(url, body, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('crearEjercicio', []))
      );
  }

  // Método para eliminar ejercicios fiscales para una empresa específica
  eliminarEjercicio(idEjercicio: number): Observable<any> {
    const url = `${this.baseUrl}${'repse/empresa'}/ejercicio/${idEjercicio}`;
    return this.http.delete<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('crearEjercicio', []))
      );
  }

  // Método para obtener todos los ejercicios asociados a una empresa específica
  obtenerEjercicios(idEmpresa: number, idSubsidiaria: number): Observable<any> {
    const url = `${this.baseUrl}${'repse/empresa'}/${idEmpresa}/ejercicio?idSubsidiaria=${idSubsidiaria}`;
    return this.http.get<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('obtenerEjercicios', []))
      );
  }

  // Método para crear los detalles de los periodos de un ejercicio existente
  crearPeriodo(idEjercicio: number, periodo: any): Observable<any> {
    const url = `${this.baseUrl}${'repse/empresa/ejercicio'}/${idEjercicio}/periodo`;
    return this.http.post<any>(url, periodo, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('crearPeriodo', []))
      );
  }

  // Método para obtener todos los periodos asociados a un ejercicio específico
  obtenerPeriodos(idEjercicio: number): Observable<any> {
    const url = `${this.baseUrl}${'repse/empresa/ejercicio'}/${idEjercicio}/periodo`;
    return this.http.get<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('obtenerPeriodos', []))
      );
  }

  // Método para obtener el idPeriodo basándose en idEjercicio y numeroMes
  obtenerIdPeriodo(idEjercicio: number, numeroMes: number): Observable<number> {
    return this.obtenerPeriodos(idEjercicio).pipe(
      map(response => {
        if (response && response.exito && response.data) {
          const periodo = response.data.find((p: any) => p.numeroMes === numeroMes);
          return periodo ? periodo.id : null;
        }
        return null;
      }),
      catchError(this.handleError('obtenerIdPeriodo', null))
    );
  }

  // Método para actualizar los detalles de un periodo existente
  actualizarPeriodo(id: number, periodo: any): Observable<any> {
    const url = `${this.baseUrl}${'repse/empresa'}/ejercicio/periodo/${id}`;
    return this.http.put<any>(url, periodo, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('actualizarPeriodo', []))
      );
  }

  // Método para eliminar un periodo por ID
  eliminarPeriodo(id: number): Observable<any> { ///repse/empresa/ejercicio/periodo/ejercicio{id}
    const url = `${this.baseUrl}${'repse/empresa'}/ejercicio/periodo/${id}`;
    return this.http.delete<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('eliminarPeriodo', []))
      );
  }

  // Método para crear documentos de estado
  crearDocumentoEstado(documentos: any[]): Observable<any> {
    const url = `${this.baseUrl}${'repse/empresa'}/ejercicio/periodo/documentoEstado`;
    const body = { documentos };
    return this.http.post<any>(url, body, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('crearDocumentoEstado', []))
      );
  }

  // Método para buscar documentos de estado por número de expediente
  buscarDocumentoEstadoPorExpediente(idExpediente: number): Observable<any> {
    const url = `${this.baseUrl}${'repse/empresa'}/ejercicio/periodo/expediente/${idExpediente}/documentoEstado`;
    return this.http.get<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('buscarDocumentoEstadoPorExpediente', []))
      );
  }

  // Método para obtener todos los documentos de estado asociados a una empresa específica
  obtenerDocumentosEstado(idEmpresa: number): Observable<any> {
    const url = `${this.baseUrl}${'repse/empresa'}/${idEmpresa}/ejercicio/periodo/documentoEstado`;
    return this.http.get<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('obtenerDocumentosEstado', []))
      );
  }

  // Método para eliminar un documento de estado por ID
  eliminarDocumentoEstado(id: number): Observable<any> {

    const url = `${this.baseUrl}${'repse/empresa'}/ejercicio/periodo/documentoEstado/${id}`;
    return this.http.delete<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('eliminarDocumentoEstado', []))
      );
  }

  modificarEstadoDocumento(payload: any, id: number): Observable<any> {
    const url = `${this.baseUrl}${'repse/empresa'}/documentoEstado/${id}`;
    return this.http.put(url, payload, this.httpOptions).pipe(
      map(response => response),
      catchError(this.handleError('modificarEstadoDocumento', []))
    );
  }

  private handleError(operation = 'operation', result?: any) {
    return (error: any): Observable<any> => {
      console.error(`${operation} failed: ${error.message}`);
      return of(result as any);
    };
  }


  //-------------------- WEBCONTENT --------------------


  crearFolder(folder: any): Observable<any> {
    return this.http.put<any>(this.webcontentUrl + 'xccm/folder/create', folder)
      .pipe(
        map(response => response),
        catchError((error: any): Observable<any> => {
          if (error.status === 500) {
            this.notificacionesService.cambiarEstatusLoader('Desactivar');
            console.error('Error 500: Internal Server Error', error);
            return of([]);
          }
          return of([]);
        })
      );
  }

  listarTodosLosFolders(identificadorPadre: string): Observable<any> {
    const params = new HttpParams()
      .set('padre', identificadorPadre);

    return this.http.get<any>(this.webcontentUrl + 'xccm/folder/get/all', { params })
      .pipe(
        map(response => response),
        catchError((error: any): Observable<any> => {
          if (error.status === 500) {
            console.error('Error 500: Internal Server Error', error);
            return of([]);
          }
          return of([]);
        })
      );
  }


  crearExpediente(data: any) {
    return this.http.post(`${this.webcontentUrl}expediente/administrar/registroExpedienteV2`, data);
  }

  darPermisosAFolder(data: any) {
    return this.http.put(`${this.webcontentUrl}xccm/folder/acl/user`, data);
  }

  darPermisosAFolders(data: any) {
    return this.http.put(`${this.webcontentUrl}xccm/folder/acls/user`, data);
  }


  porcentajeCompletez(data: any) {
    //return this.http.post(`${this.webcontentUrl}expediente/porcentajeCompletez`, data);
    //repse/empresa/cumplimiento/porcentajeCompletez
    return this.http.post(`${this.webcontentUrl}repse/empresa/cumplimiento/porcentajeCompletez`, data);

  }


  getInfoArchivo(payload: any) {
    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    headers = headers.append('Authorization', 'Bearer ' + localStorage.getItem('IES-AC-TK'));
    const payloadStr = JSON.stringify(payload);
    return from(this.encryptionService.encrypt(payloadStr))
      .pipe(
        mergeMap(encryptedData =>
          this.http.post(`${this.wsOCR}extract-entities`, encryptedData, {
            // this.http.post(`http://127.0.0.1:8000/webcontent-ocr/extract-entities`, encryptedData, {
            headers,
            responseType: 'text'
          }).pipe(
            catchError(error => {
              if (error.status === 422) {
                return throwError(() => new Error('Error 422: Unprocessable Entity'));
              }
              return throwError(() => error);
            })
          )
        ),
        mergeMap((response: string) =>
          from(this.encryptionService.decrypt(response))
        )
      );
  }


  /////////////config
  getConfiguracion() {
    return this.http.get(`${this.webcontentUrl}repse/config`);
  }


  ///////////
  //notificaciones por correo
  sendEmailToAdmin(id: string, idEmpresa?: number) {
    const empresaId = idEmpresa || Number(sessionStorage.getItem('idEmpresa')) || 0;
    let payload = {
      "objectId": id,
      "idEmpresa": empresaId,
    }
    console.log("📤 sendEmailToAdmin - Payload enviado:", payload);
    return this.http.post(`${this.webcontentUrl}repse/object/create/notifier`, payload);
  }

  checkEstatusNotificacion(idEmpresa: string) {
    return this.http.get(`${this.webcontentUrl}repse/sap/empresa/${idEmpresa}`);
  }

  // ==================== VALIDACIONES Y WARNINGS ====================

  // Método para crear/enviar warnings de validación
  enviarWarnings(payload: CrearWarningPayload): Observable<RespuestaAPI<Warning>> {
    const url = `${this.wsOCR}repse/validaciones-warnings`;

    return this.http.post<RespuestaAPI<Warning>>(url, payload, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('enviarWarnings', { exito: false, mensaje: 'Error al enviar warnings' }))
      );
  }

  // Método para obtener todos los warnings con filtros opcionales
  obtenerTodosWarnings(filtros?: FiltrosWarning): Observable<RespuestaLista<Warning>> {
    let url = `${this.wsOCR}repse/validaciones-warnings/`;

    // Agregar parámetros de consulta si existen filtros
    if (filtros) {
      const params = new URLSearchParams();
      Object.keys(filtros).forEach(key => {
        if (filtros[key] !== null && filtros[key] !== undefined) {
          params.append(key, String(filtros[key]));
        }
      });
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return this.http.get<RespuestaLista<Warning>>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('obtenerTodosWarnings', { exito: false, mensaje: 'Error al obtener warnings', data: [] }))
      );
  }

  // Método para obtener un warning por ID
  obtenerWarningPorId(id: number): Observable<RespuestaAPI<Warning>> {
    const url = `${this.wsOCR}repse/validaciones-warnings/${id}`;

    return this.http.get<RespuestaAPI<Warning>>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('obtenerWarningPorId', { exito: false, mensaje: 'Warning no encontrado' }))
      );
  }

  // Método para obtener warnings por Object ID (UUID)
  obtenerWarningsPorObjectId(objectId: string): Observable<RespuestaLista<Warning>> {
    const url = `${this.wsOCR}repse/validaciones-warnings/object-id/${objectId}`;

    return this.http.get<RespuestaLista<Warning>>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('obtenerWarningsPorObjectId', { exito: false, mensaje: 'Error al obtener warnings', data: [] }))
      );
  }

  // Método para obtener warnings por Expediente
  obtenerWarningsPorExpediente(expediente: number): Observable<any> {
    const url = `${this.wsOCR}repse/validaciones-warnings/?expediente=${expediente}`;

    return this.http.get<any>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('obtenerWarningsPorExpediente', { items: [], total: 0, page: 1, page_size: 100 }))
      );
  }

  // Método para actualizar un warning completo
  actualizarWarning(id: number, payload: ActualizarWarningPayload): Observable<RespuestaAPI<Warning>> {
    const url = `${this.wsOCR}repse/validaciones-warnings/${id}`;

    return this.http.put<RespuestaAPI<Warning>>(url, payload, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('actualizarWarning', { exito: false, mensaje: 'Error al actualizar warning' }))
      );
  }

  // Método para marcar un warning como leído
  marcarWarningComoLeido(id: number): Observable<RespuestaAPI<Warning>> {
    const url = `${this.wsOCR}repse/validaciones-warnings/${id}/marcar-leido`;

    return this.http.patch<RespuestaAPI<Warning>>(url, {}, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('marcarWarningComoLeido', { exito: false, mensaje: 'Error al marcar como leído' }))
      );
  }

  // Método para cambiar el status de un warning
  cambiarStatusWarning(id: number, payload: CambiarStatusPayload): Observable<RespuestaAPI<Warning>> {
    const url = `${this.wsOCR}repse/validaciones-warnings/${id}/cambiar-status`;

    return this.http.patch<RespuestaAPI<Warning>>(url, payload, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('cambiarStatusWarning', { exito: false, mensaje: 'Error al cambiar status' }))
      );
  }

  // Método para eliminar un warning (lógico o permanente)
  eliminarWarning(id: number, permanente: boolean = false): Observable<RespuestaAPI<any>> {
    let url = `${this.wsOCR}repse/validaciones-warnings/${id}`;

    if (permanente) {
      url += '?permanente=true';
    }

    return this.http.delete<RespuestaAPI<any>>(url, this.httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError('eliminarWarning', { exito: false, mensaje: 'Error al eliminar warning' }))
      );
  }

}
